/**
 * Phase N — /team
 *
 * A multidisciplinary team of educational strategists, transfer specialists, and
 * technology partners. Only the founder profile is populated. Additional adviser
 * cards are rendered as "coming soon" skeletons — DO NOT invent names.
 *
 * TODO: swap MonogramMark placeholders when founder provides real headshots.
 * TODO: swap adviser cards from "coming soon" as new team members join.
 */
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import LegalReviewBanner from "@/components/LegalReviewBanner";
import MonogramMark from "@/components/MonogramMark";

function TeamCard({ initials, subtitle, name, role, bio, testId, comingSoon = false }) {
  return (
    <div
      className="border border-stone/30 bg-bone p-7 flex flex-col"
      data-testid={testId}
    >
      <div className="flex justify-center mb-5">
        <MonogramMark initials={initials} size="md" subtitle={subtitle} />
      </div>
      <h3 className="font-serif text-2xl text-ink tracking-tight text-center">{name}</h3>
      <div className="mt-1 text-[10px] uppercase tracking-[0.22em] text-brass text-center">{role}</div>
      <p className="mt-4 text-sm text-ink-soft leading-relaxed text-center">{bio}</p>
      {comingSoon && (
        <div className="mt-5 text-[10px] uppercase tracking-[0.22em] text-stone text-center font-semibold">
          Profile forthcoming
        </div>
      )}
    </div>
  );
}

export default function Team() {
  return (
    <Layout>
      <SEO
        title="Team | Beyond Valencia"
        description="A multidisciplinary team of transfer strategists, admissions researchers, and technology partners building the transfer intelligence layer American higher education has been missing."
        canonical="/team"
      />
      <LegalReviewBanner />

      {/* Hero */}
      <section className="bg-ivy text-bone" data-testid="team-hero">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-20 pb-16">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brass-soft font-semibold">Team</div>
          <h1
            className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
            data-testid="team-h1"
          >
            A multidisciplinary team of strategists, researchers, and technology partners.
          </h1>
          <p className="mt-6 max-w-2xl text-bone-dim text-lg leading-relaxed">
            Beyond Valencia is delivered by a small, disciplined team supporting a growing community of transfer
            students. Additional profiles will appear here as new advisers join.
          </p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
        {/* Founder */}
        <section data-testid="team-founder-section">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">Leadership</div>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-ivy tracking-tight">Founder</h2>
          <div className="mt-8 grid md:grid-cols-2 gap-8 items-center border border-stone/30 bg-bone p-8">
            {/* TODO: swap MonogramMark for real founder headshot when provided */}
            <div className="flex justify-center">
              <MonogramMark initials="TA" size="lg" subtitle="Founder & CEO" />
            </div>
            <div>
              <h3 className="font-serif text-3xl text-ivy tracking-tight" data-testid="team-founder-name">Taimur Ahmed</h3>
              <div className="mt-1 text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">Founder &amp; CEO</div>
              <p className="mt-4 text-ink-soft leading-relaxed">
                Harvard ALM &rsquo;17 · Former Harvard admissions interviewer · Harvard Club of Central Florida ·
                Valencia College → University of Florida → Harvard. Founded Ivy League Consulting in 2018. Founded
                Beyond Valencia to build the Florida-anchored transfer intelligence platform he wishes had existed
                when he was choosing his first Valencia semester.
              </p>
              <p className="mt-4">
                <Link to="/about#founder" className="text-ivy hover:text-brass underline text-sm font-medium" data-testid="team-founder-link">
                  Full bio on the About page →
                </Link>
              </p>
            </div>
          </div>
        </section>

        {/* Advisers — coming soon skeletons */}
        <section className="mt-16" data-testid="team-advisers-section">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">Advisers</div>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-ivy tracking-tight">
            Adviser profiles forthcoming.
          </h2>
          <p className="mt-4 text-ink-soft max-w-3xl leading-relaxed">
            Beyond Valencia works with a rotating group of transfer strategists, admissions researchers, and
            subject-matter mentors. Individual adviser profiles will be published here as each new team member
            joins and clears our editorial review.
          </p>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { initials: "TS", role: "Transfer Strategist", bio: "Profile forthcoming. Focus: Florida SUS destinations, UF/UCF/FSU pathways." },
              { initials: "AR", role: "Admissions Researcher", bio: "Profile forthcoming. Focus: verification of catalog data and articulation research." },
              { initials: "TP", role: "Technology Partner", bio: "Profile forthcoming. Focus: the verified requirement graph and diagnostic engine." },
            ].map((c) => (
              <TeamCard
                key={c.initials}
                initials={c.initials}
                subtitle={c.role}
                name="Coming soon"
                role={c.role}
                bio={c.bio}
                testId={`team-adviser-${c.initials.toLowerCase()}`}
                comingSoon
              />
            ))}
          </div>
        </section>

        {/* Partnership */}
        <section className="mt-16 border-t border-stone/30 pt-12" data-testid="team-partnership">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">Partnership</div>
          <h2 className="mt-3 font-serif text-3xl text-ivy tracking-tight">Extended team via Ivy League Consulting.</h2>
          <p className="mt-4 text-ink-soft leading-relaxed max-w-3xl">
            When a student targets Ivy, elite, or out-of-state destinations, our formal partnership with Ivy League
            Consulting brings in an additional team of elite-transfer strategists. This is additive — it does not
            remove the Florida-side plan when the data supports one.
          </p>
          <Link
            to="/partners/ivy-league-consulting"
            className="mt-6 inline-block border border-ivy text-ivy hover:bg-ivy hover:text-bone uppercase tracking-[0.18em] text-[11px] font-semibold py-3 px-6"
            data-testid="team-partnership-cta"
          >
            About the partnership →
          </Link>
        </section>

        {/* Join */}
        <section className="mt-16" data-testid="team-join-section">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">Join</div>
          <h2 className="mt-3 font-serif text-3xl text-ivy tracking-tight">Interested in advising with us?</h2>
          <p className="mt-4 text-ink-soft leading-relaxed max-w-3xl">
            Beyond Valencia is early in its journey and building deliberately. If you have direct experience in
            college transfer admissions — especially Florida SUS, elite transfer, or transfer-articulation research
            — we would like to hear from you.
          </p>
          <p className="mt-3 text-ink-soft leading-relaxed">
            {/* TODO: confirm mailbox routing before publishing */}
            Reach out at{" "}
            <a href="mailto:hello@beyondvalencia.com" className="text-ivy hover:text-brass underline" data-testid="team-join-email">
              hello@beyondvalencia.com
            </a>{" "}with a note describing your background.
          </p>
        </section>
      </div>
    </Layout>
  );
}
